import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, Pressable, ScrollView } from 'react-native-web';
import Card from '../components/Card';
import Button from '../components/Button';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import Input from '../components/Input';
import PageHeader from '../components/PageHeader';
import { colors, radius } from '../theme';
import {
  getAdminMe,
  getAdminBuyers,
  createAdminBuyer,
  deleteAdminBuyer,
  impersonateBuyer,
  stopImpersonate,
} from '../api';

export default function AdminPage() {
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [impersonating, setImpersonating] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '', password: '' });
  const [message, setMessage] = useState('');

  // Fetch current user
  const fetchCurrentUser = useCallback(async () => {
    try {
      const data = await getAdminMe();
      setCurrentUser(data);
    } catch (e) {
      console.error('Failed to fetch current user:', e);
    }
  }, []);

  // Fetch buyers list
  const fetchBuyers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAdminBuyers();
      setBuyers(data || []);
    } catch (e) {
      console.error('Failed to fetch buyers:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCurrentUser();
    fetchBuyers();
  }, [fetchCurrentUser, fetchBuyers]);

  const handleCreateBuyer = async () => {
    if (!form.email || !form.password) {
      setMessage('Email and password are required');
      return;
    }

    try {
      await createAdminBuyer({
        email: form.email,
        firstName: form.firstName,
        lastName: form.lastName,
        password: form.password,
      });
      setMessage('Buyer created successfully');
      setForm({ email: '', firstName: '', lastName: '', password: '' });
      setModalVisible(false);
      fetchBuyers();
    } catch (e) {
      setMessage('Failed to create buyer: ' + e.message);
    }
  };

  const handleDeleteBuyer = async (buyerId) => {
    if (!window.confirm('Delete this buyer?')) return;

    try {
      await deleteAdminBuyer(buyerId);
      setMessage('Buyer deleted');
      fetchBuyers();
    } catch (e) {
      setMessage('Error: ' + e.message);
    }
  };

  const handleImpersonate = async (buyerId) => {
    try {
      await impersonateBuyer({ buyerId });
      setImpersonating(buyerId);
      localStorage.setItem('X-Impersonate-User', buyerId);
      setMessage(`Now viewing as ${buyerId}`);
      
      // Optionally reload to see buyer's perspective
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (e) {
      setMessage('Error: ' + e.message);
    }
  };

  const handleStopImpersonate = async () => {
    try {
      await stopImpersonate();
      setImpersonating(null);
      localStorage.removeItem('X-Impersonate-User');
      setMessage('Stopped impersonating');
      window.location.reload();
    } catch (e) {
      setMessage('Error: ' + e.message);
    }
  };

  return (
    <View>
      <PageHeader title="Admin Panel" subtitle="Manage buyers, create users, impersonate accounts">
        <Button label="+ New Buyer" onPress={() => setModalVisible(true)} />
      </PageHeader>

      {currentUser && (
        <Card style={styles.infoCard}>
          <Text style={styles.infoTitle}>Current Context</Text>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>User:</Text>
            <Text style={styles.infoValue}>{currentUser.userId}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Role:</Text>
            <Badge label={currentUser.isAdmin ? 'Admin' : 'Buyer'} color="primary" />
          </View>
          {impersonating && (
            <View style={styles.impersonatingBox}>
              <Text style={styles.impersonatingText}>
                You are impersonating: <Text style={{ fontWeight: '700' }}>{impersonating}</Text>
              </Text>
              <Button label="Stop Impersonating" size="sm" onPress={handleStopImpersonate} style={{ marginTop: 8 }} />
            </View>
          )}
        </Card>
      )}

      {message && (
        <Card style={styles.messageCard}>
          <Text style={styles.messageText}>{message}</Text>
        </Card>
      )}

      <Card style={styles.section}>
        <Text style={styles.sectionTitle}>All Buyers</Text>
        {loading ? (
          <Text style={styles.loadingText}>Loading...</Text>
        ) : buyers.length === 0 ? (
          <Text style={styles.emptyText}>No buyers yet. Create one to get started.</Text>
        ) : (
          <ScrollView>
            {buyers.map((buyer) => (
              <View key={buyer.userId} style={styles.buyerRow}>
                <View style={styles.buyerInfo}>
                  <Text style={styles.buyerEmail}>{buyer.email}</Text>
                  <Text style={styles.buyerName}>
                    {buyer.firstName} {buyer.lastName}
                  </Text>
                </View>
                <View style={styles.buyerActions}>
                  <Button
                    label="Impersonate"
                    size="sm"
                    variant="primary"
                    onPress={() => handleImpersonate(buyer.userId)}
                    style={{ marginRight: 8 }}
                  />
                  <Button
                    label="Delete"
                    size="sm"
                    variant="ghost"
                    onPress={() => handleDeleteBuyer(buyer.userId)}
                  />
                </View>
              </View>
            ))}
          </ScrollView>
        )}
      </Card>

      {/* Create Buyer Modal */}
      <Modal visible={modalVisible} title="Create New Buyer" onClose={() => setModalVisible(false)}>
        <Input
          label="Email"
          value={form.email}
          onChangeText={(v) => setForm({ ...form, email: v })}
          placeholder="buyer@example.com"
        />
        <Input
          label="First Name"
          value={form.firstName}
          onChangeText={(v) => setForm({ ...form, firstName: v })}
          placeholder="John"
        />
        <Input
          label="Last Name"
          value={form.lastName}
          onChangeText={(v) => setForm({ ...form, lastName: v })}
          placeholder="Doe"
        />
        <Input
          label="Password"
          value={form.password}
          onChangeText={(v) => setForm({ ...form, password: v })}
          placeholder="Enter password"
          secureTextEntry
        />
        <Button label="Create Buyer" onPress={handleCreateBuyer} style={{ marginTop: 16 }} />
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  infoCard: {
    marginBottom: 24,
    backgroundColor: 'rgba(99,102,241,0.05)',
    borderColor: colors.primary,
  },
  infoTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.primary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 12,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
  },
  infoLabel: {
    width: 80,
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  infoValue: {
    flex: 1,
    fontSize: 13,
    color: colors.text,
    fontFamily: 'monospace',
  },
  impersonatingBox: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: colors.warning,
  },
  impersonatingText: {
    fontSize: 13,
    color: colors.warning,
    fontWeight: '500',
  },
  messageCard: {
    marginBottom: 16,
    backgroundColor: 'rgba(34,197,94,0.1)',
    borderColor: colors.success,
  },
  messageText: {
    fontSize: 13,
    color: colors.success,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.white,
    marginBottom: 16,
  },
  loadingText: {
    fontSize: 14,
    color: colors.textMuted,
  },
  emptyText: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  buyerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 12,
    marginBottom: 8,
    backgroundColor: 'rgba(255,255,255,0.02)',
    borderRadius: radius.sm,
  },
  buyerInfo: {
    flex: 1,
  },
  buyerEmail: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.text,
    fontFamily: 'monospace',
  },
  buyerName: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 2,
  },
  buyerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});
